import type { ReactNode } from "react";
import type * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InlineSvg } from "@/components/ui/inline-svg";
import logoSvg from "@/assets/icons/logo3.svg?raw";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown } from "lucide-react";
import { languageOptions, type SupportedLanguage } from "@/i18n/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface OnboardingLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  description?: string;
  showLogo?: boolean;
}

export function OnboardingLayout({
  children,
  title,
  subtitle,
  description,
  showLogo = true,
}: OnboardingLayoutProps) {
  const { i18n } = useTranslation();
  const currentLanguage = (i18n.language?.split("-")[0] || "en") as SupportedLanguage;
  const currentOption =
    languageOptions.find((opt) => opt.code === currentLanguage) || languageOptions[0];

  const handleLanguageChange = (languageCode: SupportedLanguage) => {
    i18n.changeLanguage(languageCode);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-white px-4 sm:px-6 py-8 sm:py-12 relative"
      style={{
        backgroundImage: `radial-gradient(circle, #dfdfdf 1px, transparent 1px)`,
        backgroundSize: "16px 16px",
      }}
    >
      {/* Language Switcher Dropdown - Responsive */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
              aria-label="Switch language"
            >
              <Globe className="h-4 w-4 text-gray-600 shrink-0" />
              <span className="text-gray-700 hidden sm:inline">{currentOption.nativeName}</span>
              <span className="text-gray-700 sm:hidden">{currentOption.code.toUpperCase()}</span>
              <ChevronDown className="h-3.5 w-3.5 text-gray-500 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-32 sm:w-36">
            {languageOptions.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={cn(
                  "cursor-pointer",
                  currentLanguage === lang.code && "bg-gray-100 font-medium",
                )}
              >
                {lang.nativeName}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="w-full relative z-10">
        {/* Logo and Brand */}
        {showLogo && (
          <div className="text-center space-y-2 sm:space-y-3 mb-8 sm:mb-10">
            <InlineSvg content={logoSvg} size={108} className="text-black leading-none" />
            {title && (
              <h1 className="text-[24px] sm:text-[32px] text-black font-bold leading-tight px-4">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="text-[20px] sm:text-[24px] text-black font-bold px-4">{subtitle}</p>
            )}
            {description && (
              <p className="text-[13px] sm:text-[14px] text-gray-400 font-normal px-4">
                {description}
              </p>
            )}
          </div>
        )}

        {/* Main Content */}
        {children}
      </div>
    </div>
  );
}

interface OnboardingCardProps {
  children: ReactNode;
  className?: string;
  width?: string;
  height?: string;
}

export function OnboardingCard({
  children,
  className = "",
  width = "526px",
  height,
}: OnboardingCardProps) {
  return (
    <div
      className={`bg-white rounded-[10px] border border-[#DFDFDF] mx-auto p-4 sm:p-6 md:p-8 shadow-[0px_2px_4px_0px_rgba(228,228,228,0.6)] ${className}`}
      style={{
        maxWidth: width,
        width: "100%",
        ...(height && { minHeight: height }),
      }}
    >
      {children}
    </div>
  );
}

interface OnboardingFooterProps {
  children: ReactNode;
}

export function OnboardingFooter({ children }: OnboardingFooterProps) {
  return <div className="text-center text-[13px] text-gray-500 leading-relaxed">{children}</div>;
}

// ==================== Onboarding Specific Components ====================

interface OnboardingLabelProps extends React.ComponentProps<typeof Label> {
  required?: boolean;
}

export function OnboardingLabel({ className, required, children, ...props }: OnboardingLabelProps) {
  return (
    <Label className={cn("text-base font-normal text-gray-900 mb-2 block", className)} {...props}>
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </Label>
  );
}

interface OnboardingInputProps extends React.ComponentProps<typeof Input> {
  icon?: ReactNode;
}

export function OnboardingInput({ className, icon, ...props }: OnboardingInputProps) {
  if (!icon) {
    return (
      <Input
        className={cn(
          "rounded-[12px] px-[14px] h-[44px] placeholder:text-[#999999] placeholder:text-base",
          className,
        )}
        {...props}
      />
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999]">{icon}</div>
      <Input
        className={cn(
          "rounded-[12px] pl-12 pr-[14px] h-[44px] placeholder:text-[#999999] placeholder:text-base",
          className,
        )}
        {...props}
      />
    </div>
  );
}

type OnboardingTextareaProps = React.ComponentProps<typeof Textarea>;

export function OnboardingTextarea({ className, ...props }: OnboardingTextareaProps) {
  return (
    <Textarea
      className={cn("rounded-[12px] placeholder:text-[#999999] min-h-[120px]", className)}
      {...props}
    />
  );
}

interface OnboardingSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  className?: string;
}

export function OnboardingSelect({
  value,
  onValueChange,
  placeholder,
  options,
  disabled,
  className,
}: OnboardingSelectProps) {
  const { t } = useTranslation(["pages", "common"]);
  const selectPlaceholder = placeholder || t("components.shared.select");

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger className={cn("rounded-[12px] w-full !h-[44px]", className)}>
        <SelectValue placeholder={selectPlaceholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type OnboardingSelectTriggerProps = React.ComponentProps<typeof SelectTrigger>;

export function OnboardingSelectTrigger({
  className,
  children,
  ...props
}: OnboardingSelectTriggerProps) {
  return (
    <SelectTrigger
      className={cn(
        "rounded-[12px] w-full !h-[44px] px-[14px] data-[placeholder]:text-[#999999] data-[placeholder]:text-base",
        className,
      )}
      {...props}
    >
      {children}
    </SelectTrigger>
  );
}

type OnboardingButtonProps = React.ComponentProps<typeof Button>;

export function OnboardingButton({ className, ...props }: OnboardingButtonProps) {
  return (
    <Button
      className={cn("w-full h-11 text-[15px] font-medium rounded-full cursor-pointer", className)}
      {...props}
    />
  );
}

interface OnboardingDescriptionProps {
  children: ReactNode;
  className?: string;
}

export function OnboardingDescription({ children, className }: OnboardingDescriptionProps) {
  return (
    <div className={cn("text-center mt-4 mb-6 sm:mt-6 sm:mb-8 md:mb-[52px] px-4", className)}>
      {children}
    </div>
  );
}

interface OnboardingDividerProps {
  text?: string;
}

export function OnboardingDivider({ text }: OnboardingDividerProps) {
  const { t } = useTranslation(["common"]);
  const dividerText = text || t("or");

  return (
    <div className="my-[10px]">
      <div className="flex justify-center">
        <span className="text-gray-400 text-[18px] leading-[25px]">{dividerText}</span>
      </div>
    </div>
  );
}

export function OnboardingTerms() {
  const { t } = useTranslation(["pages", "common"]);

  return (
    <div className="text-center text-xs sm:text-[13px] text-gray-500 leading-relaxed max-w-[400px] mx-auto px-2">
      {t("onboarding.terms.agree")}{" "}
      <a
        href={t("onboarding.terms.tosUrl")}
        target="_blank"
        rel="noreferrer"
        className="text-black underline hover:text-gray-700"
      >
        {t("onboarding.terms.tos")}
      </a>{" "}
      {t("onboarding.terms.and")}{" "}
      <a
        href={t("onboarding.terms.privacyUrl")}
        target="_blank"
        rel="noreferrer"
        className="text-black underline hover:text-gray-700"
      >
        {t("onboarding.terms.privacy")}
      </a>
    </div>
  );
}
